import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import { usePreferredTrackingMethod } from '../../hooks/usePreferredTrackingMethod'
import SetupTrackingConnectionDrawer from './SetupTrackingConnectionDrawer'

type UsePreferredTrackingMethodType = ReturnType<typeof usePreferredTrackingMethod> & {
  openTrackingConnectionDrawer: () => void
  closeTrackingConnectionDrawer: () => void
}

const TrackingOnboardingContext = createContext<UsePreferredTrackingMethodType | undefined>(
  undefined
)

export function useTrackingOnboardingContext(): UsePreferredTrackingMethodType {
  const context = useContext(TrackingOnboardingContext)

  if (!context) {
    throw new Error('useTrackingOnboardingContext must be used within a TrackingOnboardingProvider')
  }

  return context
}

export function TrackingOnboardingProvider({ children }: { readonly children: ReactNode }) {
  const [trackingConnectionDrawerOpened, setTrackingConnectionDrawerOpened] = useState(false)

  const context = usePreferredTrackingMethod()

  const openTrackingConnectionDrawer = useCallback(() => {
    setTrackingConnectionDrawerOpened(true)
  }, [])

  const closeTrackingConnectionDrawer = useCallback(() => {
    setTrackingConnectionDrawerOpened(false)
  }, [])

  const value = useMemo(
    () => ({
      ...context,
      openTrackingConnectionDrawer,
      closeTrackingConnectionDrawer,
    }),
    [closeTrackingConnectionDrawer, context, openTrackingConnectionDrawer]
  )

  return (
    <TrackingOnboardingContext.Provider value={value}>
      {children}
      <SetupTrackingConnectionDrawer
        open={trackingConnectionDrawerOpened}
        onClose={closeTrackingConnectionDrawer}
      />
    </TrackingOnboardingContext.Provider>
  )
}
